import React, { useState, useLayoutEffect } from 'react';
import Block from 'lqa-ui/lib/Components/Block';
import Overlay from 'lqa-ui/lib/Components/Overlay';
import LayoutOverlay from 'lqa-ui/lib/Layouts/Overlay';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { QuestionCircle } from '../../common/Icons/QuestionCircle';
import { colors } from '../../common/styles/colors';
import { BodyXL } from '../../common/typography/typography';
import { GhostButton } from '../shared/Buttons/GhostButton/GhostButton';
import { ExternalLink } from '../shared/Buttons/ExternalLink/ExternalLink';
import { TutorialVideoList } from './TutorialVideoList';
import './styles.scss';

const ZENDESK_HELP_URL = 'https://leadingquality.zendesk.com/hc/en-us';

const FRAME_ADMIN_USERHUB_AREA_SELECTOR = '.frame-admin-nav-area3';

export const TutorialVideoButton = () => {
  const { t } = useTranslation();
  const [openTutorialVideoOverlay, setTutorialVideoOpenOverlay] = useState<boolean>(false);
  const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const area = document.querySelector(FRAME_ADMIN_USERHUB_AREA_SELECTOR);
    if (!area) {
      return;
    }
    const slot = document.createElement('div');
    slot.className = 'tutorial-video-support-nav-slot';
    area.insertBefore(slot, area.firstChild);
    setHeaderSlot(slot);
    return () => {
      slot.remove();
      setHeaderSlot(null);
    };
  }, []);

  return (
    <React.Fragment>
      <Overlay isOpen={openTutorialVideoOverlay} layout="focusview" doClose={() => {}}>
        <LayoutOverlay
          outsideClickClose={true}
          outsideClickClosefn={() => {
            setTutorialVideoOpenOverlay(false);
          }}
          // lqa-ui's `LayoutOverlay` types `title` as `string` but
          // the underlying `Block` renders any ReactNode at runtime.
          // Cast through `unknown` to pass JSX without changing the
          // shared lib's type def.
          title={
            (
              <span className="tutorial-video-overlay-title">
                <BodyXL color="textWhite">{t('common.SUPPORT_VIDEOS.TITLE')}</BodyXL>
                <ExternalLink href={ZENDESK_HELP_URL} newTab={true}>
                  {t('common.SUPPORT_VIDEOS.CONTACT_US_AND_FAQS') as string}
                </ExternalLink>
              </span>
            ) as unknown as string
          }
          size="wide"
          isFocusOnFirstButton={true}
          buttonIcons={[
            {
              icon: 'close',
              onClick: () => {
                setTutorialVideoOpenOverlay(false);
              },
            },
          ]}
        >
          <div className="alternate-blocks">
            <Block>
              <TutorialVideoList setTutorialVideoOpenOverlay={setTutorialVideoOpenOverlay} />
            </Block>
          </div>
        </LayoutOverlay>
      </Overlay>
      {headerSlot &&
        createPortal(
          <GhostButton
            type="button"
            className="tutorial-video-support-fab tutorial-video-support-fab--nav"
            aria-label={t('common.SUPPORT_VIDEOS.TITLE')}
            leftIcon={<QuestionCircle size="32" fillColor={colors.undefined} />}
            onClick={() => setTutorialVideoOpenOverlay(true)}
          />,
          headerSlot
        )}
    </React.Fragment>
  );
};
